
import React from "react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Coffee, Server, PenTool, FileText, FileBarChart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";

const Donate = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background w-full">
      <Header 
        pageName="Support 3DP Hub"
      />
      
      <main className="flex-1 py-6 pt-[64px]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-3">Help Build Our Community</h1>
            <p className="text-lg text-content">
              Free access to guides, tools and data for everyone.
            </p>
          </div>
          
          <div className="bg-card rounded-lg p-6 shadow-sm border border-border mb-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <h2 className="text-2xl font-bold">Help build the platform</h2>
                  <Button 
                    size="lg" 
                    className="gap-2 w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl" 
                    asChild
                  >
                    <a href="https://ko-fi.com/dfbpurcell" target="_blank" rel="noopener noreferrer">
                      <Coffee className="w-5 h-5" />
                      Donate
                    </a>
                  </Button>
                </div>
                <p className="mb-3 text-content">
                  3DP Hub is a passion project created to help the 3D printing community find, share and validate information about parts, upgrades, repair and maintenance.
                </p>
                <p className="text-content">
                  Your support helps to cover server costs, development time, and fuels the addition of new features and content.
                </p>
              </div>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold mb-4 text-center">How Your Support Helps</h2>
          
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <Card className="shadow-sm border border-border">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <Server className="w-8 h-8 mb-3 text-primary" />
                  <h3 className="text-lg font-semibold mb-2">Server Costs</h3>
                  <p className="text-sm text-content">
                    Helps keep our database and website running smoothly with fast response times.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-sm border border-border">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <PenTool className="w-8 h-8 mb-3 text-primary" />
                  <h3 className="text-lg font-semibold mb-2">New Features</h3>
                  <p className="text-sm text-content">
                    Enables us to develop new tools and features to help the community.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-sm border border-border">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <FileText className="w-8 h-8 mb-3 text-primary" />
                  <h3 className="text-lg font-semibold mb-2">Content Creation</h3>
                  <p className="text-sm text-content">
                    Funds the creation of high-quality guides and documentation.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="mb-6 bg-muted rounded-lg p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-3">
              <h2 className="text-xl font-bold">Community Transparency</h2>
              <Button variant="ghost" className="gap-2 border border-gray-300 dark:border-gray-600 bg-background hover:bg-accent hover:text-accent-foreground w-full md:w-auto" asChild>
                <Link to="/roadmap">
                  <FileBarChart className="w-5 h-5" />
                  View our roadmap
                </Link>
              </Button>
            </div>
            <h3 className="text-lg font-semibold mb-2">How we use your donations</h3>
            <p className="mb-3 text-content">
              We believe in full transparency. Here's how we currently allocate funds:
            </p>
            <ul className="list-disc pl-6 mb-4 text-content">
              <li className="mb-2">100% - Server costs and database development</li>
              <li className="mb-2">When the database is complete, more features will be added.</li>
            </ul>
            <p className="text-content">
              We're committed to using your donations responsibly to benefit the entire 3D printing community.
            </p>
          </div>
          
          <div className="mb-6 bg-card rounded-lg p-6 shadow-sm border border-border">
            <h3 className="text-xl font-bold mb-3 text-center">Support Options</h3>
            <p className="text-center text-content mb-4">
              You can make a one-time donation or become a monthly supporter through Ko-fi.
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl" 
                asChild
              >
                <a href="https://ko-fi.com/dfbpurcell" target="_blank" rel="noopener noreferrer">
                  <Coffee className="w-5 h-5" />
                  Donate
                </a>
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="bg-card border-t border-border py-4 w-full">
        <div className="container">
          <p className="text-sm text-center text-content">
            3DP Hub | The ultimate directory for 3D printer parts and upgrades
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Donate;
